import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { DAYS } from '@/lib/pickupHours'
import type { DayHours, DayKey, PickupInfo } from '@/types'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Toggle from '@/components/ui/Toggle'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/toast'

interface Draft {
  enabled: boolean
  days: DayKey[]
  open: string
  close: string
  /** Present = "different hours" ticked for that day. */
  overrides: Partial<Record<DayKey, DayHours>>
  name: string
  phone: string
  address: string
  city: string
  state: string
  postcode: string
  note: string
}

const chip = (active: boolean) =>
  cn(
    'rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors',
    active ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-foreground hover:bg-foreground/5',
  )

/**
 * The self pickup card and schedule: an on/off switch, open days, default
 * hours with per-day exceptions, the collection address and a note. Seeded
 * once from the server; afterwards the admin's edits win.
 */
export default function SelfPickupTab({
  initial,
  states,
  onSaved,
}: {
  initial: PickupInfo
  states: string[]
  onSaved: () => void
}) {
  const toast = useToast()
  const [form, setForm] = useState<Draft>(() => ({
    enabled: initial.enabled,
    days: initial.days ?? [],
    open: initial.open ?? '',
    close: initial.close ?? '',
    overrides: { ...(initial.day_hours ?? {}) },
    name: initial.name ?? '',
    phone: initial.phone ?? '',
    address: initial.address ?? '',
    city: initial.city ?? '',
    state: initial.state ?? '',
    postcode: initial.postcode ?? '',
    note: initial.note ?? '',
  }))

  const set = <K extends keyof Draft>(key: K) => (value: Draft[K]) => setForm((f) => ({ ...f, [key]: value }))

  const toggleDay = (day: DayKey) =>
    setForm((f) => ({
      ...f,
      days: f.days.includes(day) ? f.days.filter((d) => d !== day) : [...f.days, day],
    }))

  // Ticking "different hours" seeds the row with the current defaults so
  // the admin only changes what differs.
  const toggleOverride = (day: DayKey) =>
    setForm((f) => {
      const overrides = { ...f.overrides }
      if (overrides[day]) delete overrides[day]
      else overrides[day] = { from: f.open, to: f.close }
      return { ...f, overrides }
    })

  const setOverride = (day: DayKey, patch: Partial<DayHours>) =>
    setForm((f) => ({
      ...f,
      overrides: { ...f.overrides, [day]: { from: '', to: '', ...f.overrides[day], ...patch } },
    }))

  const save = useMutation({
    mutationFn: async () => {
      // Canonical order, and only complete overrides for days that are open.
      const days = DAYS.map((d) => d.key).filter((d) => form.days.includes(d))
      const dayHours: Partial<Record<DayKey, DayHours>> = {}
      for (const d of days) {
        const o = form.overrides[d]
        if (o?.from && o?.to) dayHours[d] = { from: o.from, to: o.to }
      }
      return (
        await api.patch('/admin/delivery/pickup', {
          enabled: form.enabled,
          days,
          open: form.open || null,
          close: form.close || null,
          day_hours: Object.keys(dayHours).length ? dayHours : null,
          name: form.name,
          phone: form.phone || null,
          address: form.address || null,
          city: form.city || null,
          state: form.state || null,
          postcode: form.postcode || null,
          note: form.note || null,
        })
      ).data
    },
    onSuccess: (res) => {
      onSaved()
      toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const openDays = DAYS.filter((d) => form.days.includes(d.key))

  return (
    <Card>
      <Toggle
        checked={form.enabled}
        onChange={set('enabled')}
        label="Offer self pickup"
        hint="Lets customers collect their order instead of paying for delivery."
      />

      <div className={cn('mt-5 max-w-2xl space-y-5', !form.enabled && 'pointer-events-none opacity-50')}>
        <div>
          <p className="mb-1.5 text-sm text-foreground">Pickup days</p>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => (
              <button
                key={d.key}
                type="button"
                aria-pressed={form.days.includes(d.key)}
                onClick={() => toggleDay(d.key)}
                className={chip(form.days.includes(d.key))}
              >
                {d.short}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Default open time">
            <Input type="time" value={form.open} onChange={(e) => set('open')(e.target.value)} />
          </Field>
          <Field label="Default close time">
            <Input type="time" value={form.close} onChange={(e) => set('close')(e.target.value)} />
          </Field>
        </div>

        {openDays.length > 0 && (
          <div>
            <p className="text-sm text-foreground">Different hours on a day?</p>
            <p className="mb-2 text-xs text-muted">
              Selected days use the default time above unless you set different hours here.
            </p>
            <div className="space-y-2">
              {openDays.map((d) => {
                const o = form.overrides[d.key]
                return (
                  // Line one: checkbox + day. Line two (or, from sm, the same
                  // line): from - to. Two native time inputs do not fit beside
                  // the label at 375px.
                  <div key={d.key} className="grid gap-2 rounded-lg border border-line px-3 py-2 sm:grid-cols-[7rem_auto_1fr] sm:items-center">
                    <span className="text-sm font-semibold">{d.long}</span>
                    <label className="flex items-center gap-2 text-sm text-muted">
                      <input
                        type="checkbox"
                        checked={Boolean(o)}
                        onChange={() => toggleOverride(d.key)}
                        className="size-4 accent-primary"
                      />
                      Different hours
                    </label>
                    {o && (
                      // Stacked below sm: a native time input keeps its intrinsic width,
                      // and two side by side clip their AM/PM inside a 375px card.
                      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
                        <Input type="time" value={o.from} onChange={(e) => setOverride(d.key, { from: e.target.value })} className="min-w-0" />
                        <span className="hidden text-muted sm:inline">-</span>
                        <Input type="time" value={o.to} onChange={(e) => setOverride(d.key, { to: e.target.value })} className="min-w-0" />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className="border-t border-line pt-5">
          <p className="text-sm font-bold">Pickup location</p>
          <p className="mb-3 text-xs text-muted">Shown to customers at checkout and on their order.</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Shop / contact name">
              <Input value={form.name} onChange={(e) => set('name')(e.target.value)} />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="012-345 6789" />
            </Field>
            <Field label="Address" className="sm:col-span-2">
              <Textarea
                rows={2}
                value={form.address}
                onChange={(e) => set('address')(e.target.value)}
                placeholder="Lot 123, Jalan Besar"
                className="min-h-0"
              />
            </Field>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <Field label="City">
              <Input value={form.city} onChange={(e) => set('city')(e.target.value)} />
            </Field>
            <Field label="State">
              <Select
                value={form.state}
                onChange={set('state')}
                placeholder="Select state..."
                options={states.map((s) => ({ value: s, label: s }))}
              />
            </Field>
            <Field label="Postcode">
              <Input
                inputMode="numeric"
                maxLength={5}
                value={form.postcode}
                onChange={(e) => set('postcode')(e.target.value.replace(/\D/g, ''))}
                placeholder="34300"
              />
            </Field>
          </div>
        </div>

        <Field label="Note (shown to the customer)">
          <Textarea
            rows={3}
            value={form.note}
            onChange={(e) => set('note')(e.target.value)}
            placeholder="e.g. Show your order number when collecting. Closed on public holidays."
          />
        </Field>
      </div>

      <Button className="mt-5" loading={save.isPending} onClick={() => save.mutate()}>
        Save pickup details
      </Button>
    </Card>
  )
}
